import type { FacilityFlowCoreModulesOffersDtosOfferPositionDto as OfferPositionDto } from '../../../api/generated/models';
import { BillingKinds, needsReview, type BillingKind, type BillingRule } from '../../../utils/billingKind';

export interface BreakdownPosition {
  key: string;
  title: string;
  interval: string;
  netAmount: number;
  kind: BillingKind;
  billingRule: BillingRule;
}

/** Offer positions grouped by billing kind, in display order. */
export interface OfferPositionSplit {
  /** Monthly all year — summed into the monthly flat rate („Monatspauschale"). */
  monthly: BreakdownPosition[];
  /** Monthly within a season only (e.g. winter service Nov–Mar) — not in every month's invoice. */
  seasonal: BreakdownPosition[];
  quarterly: BreakdownPosition[];
  yearly: BreakdownPosition[];
  oneOff: BreakdownPosition[];
  byEffort: BreakdownPosition[];
  /** No kind yet („zu prüfen") — blocks billing of the object. */
  review: BreakdownPosition[];
}

/**
 * Groups offer positions by the billing kind the backend returns (`recurrenceKind`) — the same
 * kind the invoice bills by, so the flat rate here is what the monthly invoice contains
 * (FAFD-27). The interval text is description only and plays no part.
 */
export function splitOfferPositions(positions: OfferPositionDto[]): OfferPositionSplit {
  const split: OfferPositionSplit = {
    monthly: [], seasonal: [], quarterly: [], yearly: [], oneOff: [], byEffort: [], review: [],
  };
  positions.forEach((p, index) => {
    const kind = p.recurrenceKind ?? BillingKinds.Unknown;
    const position: BreakdownPosition = {
      // why: an id-less position must not collide with another one as React key.
      key: p.id || `position-${index}`,
      title: p.title ?? '',
      interval: p.interval ?? '',
      netAmount: p.netAmount ?? 0,
      kind,
      billingRule: p.billingRule ?? {},
    };
    // why: the backend's billing months say whether a monthly position is billed every month;
    // a season (e.g. Nov–Mar) is missing from the summer invoices, so it is no flat rate.
    const months = p.billingMonths?.length ?? 12;
    groupFor(split, kind, months > 0 && months < 12).push(position);
  });
  return split;
}

function groupFor(split: OfferPositionSplit, kind: BillingKind, inSeason: boolean): BreakdownPosition[] {
  if (needsReview(kind)) return split.review;
  switch (kind) {
    case BillingKinds.Monthly:
      return inSeason ? split.seasonal : split.monthly;
    case BillingKinds.Quarterly:
      return split.quarterly;
    case BillingKinds.Yearly:
      return split.yearly;
    case BillingKinds.OneOff:
      return split.oneOff;
    case BillingKinds.ByEffort:
      return split.byEffort;
    default:
      return split.review;
  }
}

export const sumNet = (positions: BreakdownPosition[]) =>
  positions.reduce((sum, p) => sum + p.netAmount, 0);
